import React, { Fragment, useCallback } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { Label } from '@atlaskit/form';
import MediaServicesScaleLargeIcon from '@atlaskit/icon/glyph/media-services/scale-large';
import EditorTextStyleIcon from '@atlaskit/icon/glyph/editor/text-style';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import PersonIcon from '@atlaskit/icon/glyph/person';

import { useBooleanFeatureFlag } from '@confluence/session-data';

import {
	ToggleField,
	ConfigPanelSubheading,
	FormFieldErrorMessage,
	useIsDisabledContext,
} from '../../../shared-components';
import { CardType } from '../../linkCardsTypes';
import { CONDITIONAL_FIELDS } from '../linkCardsConfigPanelTypes';
import type { UpdateMainFormFieldType } from '../linkCardsConfigPanelTypes';
import { CUSTOM_SITES_CARDS_EXTENSION_VISUAL_CARDS_FF } from '../../customSitesFeatureFlags';
import { checkType } from '../../LinkCardsContent';

export const DEFAULT_CARDS_TYPE = CardType.TEXT;

const { IS_AVATAR_SHOWN, IS_PUBLISH_DATE_SHOWN } = CONDITIONAL_FIELDS;

type TypeSelectOption = {
	label: React.ReactNode;
	value: CardType;
	isDisabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeFieldFlexContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectOption = styled.div({
	display: 'flex',
	gap: token('space.075', '6px'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100', '8px'),
	marginBottom: token('space.200', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToggleWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200', '8px'),
});

const i18n = defineMessages({
	typeTextLabel: {
		id: 'custom-sites-extensions.link-cards.link-cards-config-panel.type-text-label',
		defaultMessage: 'Type',
		description:
			'Label that identifies the section as the type (visual vs. text-based) select section',
	},
	visualDropDownItemLabel: {
		id: 'custom-sites-extensions.link-cards.link-cards-config-panel.type-field.drop-down.item.visual.label',
		defaultMessage: 'Visual',
		description:
			'A visible label for the select option/selection/item to change card to visual design for link card',
	},
	textBasedDropDownItemLabel: {
		id: 'custom-sites-extensions.link-cards.link-cards-config-panel.type-field.drop-down.item.text-based.label',
		defaultMessage: 'Text based',
		description:
			'A visible label for the select option/selection/item to change card to text-based design for link card',
	},
	publishDateToggleLabel: {
		id: 'custom-sites-extensions.link-cards.link-cards-config-panel.type-field.toggle.publish-date.label',
		defaultMessage: 'Show last update',
		description:
			'A visible label for the toggle to display/hide publish date for type text based link card',
	},
	avatarToggleLabel: {
		id: 'custom-sites-extensions.link-cards.link-cards-config-panel.type-field.toggle.avatar.label',
		defaultMessage: 'Show owner',
		description:
			'A visible label for the toggle to display/hide avatar for type text based link card',
	},
	typeParameterInvalidMessage: {
		id: 'custom-sites-extensions.link-cards.link-cards-config-panel.type.error.invalid-parameter',
		defaultMessage: 'Select type',
		description:
			'Message for error shown to user when a value for type is not provided or is invalid',
	},
});

type CardConfigPanelTypeFieldProps = {
	typeParameter: CardType;
	updateMainFormField: UpdateMainFormFieldType;
	isAvatarShownParameter: boolean;
	isPublishDateShownParameter: boolean;
};

export const CardConfigPanelTypeField: React.FC<CardConfigPanelTypeFieldProps> = ({
	updateMainFormField,
	typeParameter,
	isAvatarShownParameter,
	isPublishDateShownParameter,
}) => {
	const intl = useIntl();
	const isDisabled = useIsDisabledContext();
	const isLinkCardsExtensionVisualCardsEnabled = useBooleanFeatureFlag(
		CUSTOM_SITES_CARDS_EXTENSION_VISUAL_CARDS_FF,
	);

	const typeSelectOptions: TypeSelectOption[] = [
		{
			label: (
				<SelectOption>
					<MediaServicesScaleLargeIcon label="" />
					<FormattedMessage {...i18n.visualDropDownItemLabel} />
				</SelectOption>
			),
			value: CardType.VISUAL,
			isDisabled: !isLinkCardsExtensionVisualCardsEnabled,
		},
		{
			label: (
				<SelectOption>
					<EditorTextStyleIcon label="" />
					<FormattedMessage {...i18n.textBasedDropDownItemLabel} />
				</SelectOption>
			),
			value: CardType.TEXT,
		},
	];

	const selectedType = typeSelectOptions.find(({ value }) => value === typeParameter);

	const handleSelectTypeChange = useCallback(
		(selectedLabel: any) => {
			if (selectedLabel.value === CardType.TEXT) {
				updateMainFormField({
					type: selectedLabel.value,
					isPublishDateShown: false,
					isAvatarShown: true,
				});
			} else if (selectedLabel.value === CardType.VISUAL) {
				updateMainFormField(
					{ type: selectedLabel.value },
					{
						fieldsToRemove: [IS_AVATAR_SHOWN, IS_PUBLISH_DATE_SHOWN],
					},
				);
			}
		},
		[updateMainFormField],
	);

	const handleAvatarToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_AVATAR_SHOWN]: !isAvatarShownParameter,
		});
	}, [isAvatarShownParameter, updateMainFormField]);

	const handlePublishDateToggleChange = useCallback(() => {
		updateMainFormField({
			[IS_PUBLISH_DATE_SHOWN]: !isPublishDateShownParameter,
		});
	}, [isPublishDateShownParameter, updateMainFormField]);

	return (
		<TypeFieldFlexContainer>
			<Label htmlFor="card-type-selector">
				<ConfigPanelSubheading>
					<FormattedMessage {...i18n.typeTextLabel} />
				</ConfigPanelSubheading>
			</Label>
			<SelectWrapper>
				<Select
					id="card-type-selector"
					inputId="card-type-select-input"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="card-type-single-select"
					classNamePrefix="card-type-select"
					isSearchable={false}
					value={selectedType}
					defaulValue={selectedType}
					onChange={handleSelectTypeChange}
					options={typeSelectOptions}
					isDisabled={isDisabled}
					aria-label={intl.formatMessage(i18n.typeTextLabel)}
				/>
				{!checkType(typeParameter, isLinkCardsExtensionVisualCardsEnabled) && (
					<FormFieldErrorMessage>
						<FormattedMessage {...i18n.typeParameterInvalidMessage} />
					</FormFieldErrorMessage>
				)}
			</SelectWrapper>
			<ToggleWrapper>
				{typeParameter === CardType.TEXT ? (
					<Fragment>
						<ToggleField
							label={intl.formatMessage(i18n.publishDateToggleLabel)}
							Icon={CalendarIcon}
							isChecked={isPublishDateShownParameter}
							onChange={handlePublishDateToggleChange}
						/>
						<ToggleField
							label={intl.formatMessage(i18n.avatarToggleLabel)}
							Icon={PersonIcon}
							isChecked={isAvatarShownParameter}
							onChange={handleAvatarToggleChange}
						/>
					</Fragment>
				) : null}
			</ToggleWrapper>
		</TypeFieldFlexContainer>
	);
};
